// Shipping Protection cart widget - HEXA Lighting
// Price = 5% of cart subtotal (excl. protection), rounded to nearest $1, capped $1-$50
// Default ON. Price always shown via Liquid. JS handles add/remove, then re-renders the
// cart through the theme's own SectionDynamicUpdate pipeline (the same one a quantity
// change uses) so the total, item count and free-shipping bar all stay in sync.
//
// GUARANTEE: shipping protection appears EXACTLY ONCE per order. The cart page renders
// two copies of this widget (one inside the cart drawer, one inside main-cart), and both
// used to auto-add on load at the same instant, producing a quantity-2 line (see order
// #2874). That is fixed here by three layers:
//   1. A shared serialisation lock (withSpLock) so no two cart mutations ever overlap.
//   2. Every mutation re-reads the LIVE cart first, so whichever widget runs second sees
//      the first one's line already present and does nothing.
//   3. reconcileProtection() collapses any duplicate/wrong-tier protection lines down to a
//      single quantity-1 line on every load and after every toggle - a permanent safety net.

(function () {
  'use strict';

  if (window.hexaShipProtectLoaded) return;
  window.hexaShipProtectLoaded = true;

  const SP_HANDLE   = 'shipping-protection';
  const OPT_OUT_KEY = 'hexaSpOptOut';

  // Sections re-rendered after any cart change - mirrors Dawn's cart getSectionsToRender().
  // Each entry is found in the live DOM via getElementById(id).querySelector(selector);
  // any entry whose container isn't on the current page is skipped automatically.
  const SECTION_NAMES = 'cart-icon-bubble,cart-drawer,main-cart';
  const SECTIONS_TO_RENDER = [
    { id: 'Cart-Icon-Bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    { id: 'Cart-Drawer',      section: 'cart-drawer',      selector: '#Cart-Drawer-Details' },
    { id: 'Main-Cart',        section: 'main-cart',        selector: '#Main-Cart-Details' }
  ];

  let _variantMap = null;

  // Only re-render the cart sections actually present on the current page.
  // On a product/collection page Main-Cart isn't in the DOM, so asking the
  // server to render it is wasted time. This mirrors cart.js getSectionsToRender()
  // and is the main speed-up for the toggle.
  function presentSections() {
    return SECTIONS_TO_RENDER.filter(s => document.getElementById(s.id));
  }
  function sectionNamesParam() {
    const names = presentSections().map(s => s.section).join(',');
    return names || SECTION_NAMES; // fallback: never send an empty list
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  async function fetchVariantMap() {
    if (_variantMap) return _variantMap;
    try {
      const res = await fetch('/products/' + SP_HANDLE + '.js');
      if (!res.ok) return null;
      const data = await res.json();
      _variantMap = {};
      data.variants.forEach(v => { _variantMap[parseInt(v.option1, 10)] = v.id; });
      return _variantMap;
    } catch { return null; }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  function getVariantId(priceCents, map) {
    const dollars = Math.max(1, Math.min(50, Math.round(priceCents / 100)));
    return map[dollars];
  }

  // ── Cart API ─────────────────────────────────────────────────────────────

  // When withSections is true, Shopify renders and returns the cart sections in the
  // SAME response - so a mutation + re-render is one round-trip instead of two.
  async function cartAdd(variantId, withSections) {
    const body = { id: variantId, quantity: 1 };
    if (withSections) { body.sections = sectionNamesParam(); body.sections_url = location.pathname; }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('cart/add failed');
    return res.json();
  }

  // Set an existing line to a given quantity (0 removes it).
  async function cartChange(variantId, quantity, withSections) {
    const body = { id: String(variantId), quantity: quantity };
    if (withSections) { body.sections = sectionNamesParam(); body.sections_url = location.pathname; }
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('cart/change failed');
    return res.json();
  }

  // ── Serialised cart mutations ─────────────────────────────────────────────
  // Every protection add/remove/collapse runs through this single queue. Because
  // each queued job re-reads the live cart before it acts (inside reconcileProtection),
  // two widget instances - the drawer and the cart page - can never both add
  // protection: whichever runs second sees the first one's line and no-ops. This is
  // the primary defence against the quantity-2 duplicate.
  let _spLock = Promise.resolve();
  function withSpLock(job) {
    const run = _spLock.then(job, job);       // run whether the previous job resolved or rejected
    _spLock = run.then(() => {}, () => {});    // keep the queue alive regardless of outcome
    return run;
  }

  // ── Single source of truth: reconcile the cart to the desired protection state ──
  // wantOn === true  -> protection present exactly once, at the correct price tier, qty 1
  // wantOn === false -> protection absent entirely
  // Always reads the live cart first, so it both prevents the double-add race AND
  // repairs any pre-existing duplicate (qty > 1, or more than one tier line).
  // Returns the last mutation's response (with .sections) for re-render, or {noop:true}
  // when the cart is already in the desired state.
  async function reconcileProtection(wantOn, desiredVid) {
    const cart = await fetchCart();
    const spItems = cart.items.filter(i => i.handle === SP_HANDLE);

    // Build the exact list of operations needed to reach the desired state.
    const ops = []; // { vid, qty, isAdd? }

    if (!wantOn || !desiredVid) {
      // Remove every protection line. (!desiredVid guards a missing price tier: never
      // add an undefined variant - just make sure nothing stale is left behind.)
      spItems.forEach(i => ops.push({ vid: i.variant_id, qty: 0 }));
    } else {
      const correct = spItems.filter(i => i.variant_id === desiredVid);
      const wrong   = spItems.filter(i => i.variant_id !== desiredVid);

      // Drop any protection line that is the wrong price tier.
      wrong.forEach(i => ops.push({ vid: i.variant_id, qty: 0 }));

      if (correct.length === 0) {
        ops.push({ vid: desiredVid, qty: 1, isAdd: true }); // none present: add exactly one
      } else if (correct[0].quantity !== 1) {
        ops.push({ vid: desiredVid, qty: 1 });              // present but qty != 1: collapse to 1
      }
    }

    if (ops.length === 0) return { noop: true }; // already correct - nothing to do

    // Run the ops in order; only the LAST one asks the server for rendered sections.
    let resp = null;
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      const withSections = (i === ops.length - 1);
      resp = op.isAdd
        ? await cartAdd(op.vid, withSections)
        : await cartChange(op.vid, op.qty, withSections);
    }
    return resp;
  }

  // ── Cart refresh (uses the theme's own renderer) ──────────────────────────
  // Fetches server-rendered sections and applies them via SectionDynamicUpdate -
  // the exact path Dawn uses for a quantity change - so the drawer/cart total,
  // item count and free-shipping bar all update reliably. The hidden placeholder
  // protection row in cart-details keeps Dawn's line-count check correct, so a
  // full re-render is safe. Falls back to a subtotal-only swap if the theme
  // helper is unavailable for any reason.

  function applySections(sections) {
    if (!sections) return false;
    if (typeof SectionDynamicUpdate !== 'undefined' && SectionDynamicUpdate.updateSections) {
      SectionDynamicUpdate.updateSections(SECTIONS_TO_RENDER, sections);
    } else {
      // Defensive fallback: swap only the subtotal if the theme helper is missing.
      for (const [key, containerId] of [['main-cart', 'Main-Cart-Details'], ['cart-drawer', 'Cart-Drawer-Details']]) {
        if (!sections[key]) continue;
        const tmp      = new DOMParser().parseFromString(sections[key], 'text/html');
        const freshSub = tmp.querySelector('.footer-subtotal');
        if (!freshSub) continue;
        const liveContainer = document.getElementById(containerId);
        if (!liveContainer) continue;
        const liveSub = liveContainer.querySelector('.footer-subtotal');
        if (liveSub) liveSub.innerHTML = freshSub.innerHTML;
      }
    }

    // Notify other cart-aware components (free-shipping bar, etc.) of the change.
    try {
      if (typeof webvista !== 'undefined' && webvista.publish && typeof PUB_SUB_EVENTS !== 'undefined') {
        webvista.publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shipping-protection' });
      }
    } catch (e) { /* pub-sub optional - never block the refresh */ }
    return true;
  }

  // Used only when a cart mutation didn't already return sections (rare fallback path).
  async function refreshCartSections() {
    try {
      const res = await fetch('/cart?sections=' + sectionNamesParam());
      if (!res.ok) return;
      applySections(await res.json());
    } catch (e) {
      console.warn('[ShipProtect] Cart refresh failed', e);
    }
  }

  // Apply a reconcile result: re-render if a real mutation happened, skip on no-op.
  async function applyReconcileResult(resp) {
    if (!resp || resp.noop) return;
    if (!applySections(resp.sections)) await refreshCartSections();
  }

  // Optimistically rewrite the visible cart total so the price reacts the INSTANT the
  // toggle is tapped, before the network round-trip. Reconciled by the server response.
  function setDisplayedTotal(cents, currency) {
    if (cents < 0 || !currency || typeof webvista === 'undefined' || !webvista.formatPriceAmount) return;
    const formatted = webvista.formatPriceAmount(cents, currency);
    if (!formatted) return;
    document.querySelectorAll(
      '#Cart-Drawer-Details .footer-subtotal .total-price, #Main-Cart-Details .footer-subtotal .total-price'
    ).forEach(t => { t.textContent = formatted; });
  }

  // ── Widget init ───────────────────────────────────────────────────────────

  async function initWidget(el) {
    if (el.dataset.spInit) return;
    el.dataset.spInit = '1';

    const sectionId = el.dataset.section;
    const checkbox  = document.getElementById('SP-Checkbox-' + sectionId);
    if (!checkbox) return;

    const map = await fetchVariantMap();
    if (!map) { el.style.display = 'none'; return; }

    // Price is Liquid-rendered - no extra fetch needed to know the tier.
    const priceCents = parseInt(el.dataset.spPrice, 10) || 0;
    const correctVid = getVariantId(priceCents, map);
    const optedOut   = localStorage.getItem(OPT_OUT_KEY) === '1';

    // Desired state for this load:
    //   no real products in cart (price $0) -> OFF and clear any stray line
    //   user previously opted out           -> OFF
    //   otherwise (default)                 -> ON
    const desiredOn = priceCents > 0 && !optedOut;
    checkbox.checked = desiredOn;

    // Reconcile the live cart to the desired state. Serialised + live-cart-read, so the
    // two widget instances on the cart page can never both add, and any pre-existing
    // duplicate is collapsed to a single qty-1 line. This is the fix for order #2874.
    el.classList.add('is-loading');
    try {
      const resp = await withSpLock(() => reconcileProtection(desiredOn, correctVid));
      await applyReconcileResult(resp);
      el.dataset.inCart = desiredOn ? 'true' : 'false';
      el.dataset.currentVariant = (desiredOn && correctVid) ? String(correctVid) : '';
    } catch (e) {
      console.warn('[ShipProtect] Init reconcile failed', e);
    }
    el.classList.remove('is-loading');

    // No real products in the cart: protection is not applicable, no toggle handler needed.
    if (priceCents <= 0) return;

    // Opted-out but ON explicitly clears the flag once protection is showing correctly.
    if (desiredOn) localStorage.removeItem(OPT_OUT_KEY);

    // ── Toggle handler ────────────────────────────────────────────────────
    checkbox.addEventListener('change', async (e) => {
      const wantsOn       = e.target.checked;
      const priceCentsNow = parseInt(el.dataset.spPrice, 10) || 0;
      const vid           = getVariantId(priceCentsNow, map);

      // Instant feedback: the switch + grey-out are already live (CSS), and we
      // optimistically rewrite the total here so the price moves on tap. The server
      // response below reconciles the exact figure.
      const cartTotal = parseInt(el.dataset.cartTotal, 10);
      if (!isNaN(cartTotal)) {
        setDisplayedTotal(wantsOn ? cartTotal + priceCentsNow : cartTotal - priceCentsNow, el.dataset.currency);
      }

      el.classList.add('is-loading');
      e.target.disabled = true;

      try {
        if (wantsOn) localStorage.removeItem(OPT_OUT_KEY);
        else         localStorage.setItem(OPT_OUT_KEY, '1');

        // One reconcile handles every case: add when absent, swap tier when the price
        // changed, collapse a duplicate, or remove on opt-out - always ending at
        // "exactly one line" or "none".
        const resp = await withSpLock(() => reconcileProtection(wantsOn, vid));
        await applyReconcileResult(resp);

        el.dataset.inCart = wantsOn ? 'true' : 'false';
        el.dataset.currentVariant = (wantsOn && vid) ? String(vid) : '';
      } catch (err) {
        console.warn('[ShipProtect] Toggle failed', err);
        e.target.checked = !wantsOn;
        await refreshCartSections(); // reconcile the displayed total on failure
      } finally {
        el.classList.remove('is-loading');
        e.target.disabled = false;
      }
    });
  }

  // ── Init all unprocessed widgets ──────────────────────────────────────────

  function initAll() {
    document.querySelectorAll('.shipping-protection:not([data-sp-init])').forEach(el => initWidget(el));
  }

  // ── Observe cart content areas for Dawn's own section re-renders ──────────
  // Uses subtree so it fires whether Dawn replaces the full section or just inner content.
  // Debounced 150ms to let Dawn finish rendering before re-init.

  function observeCartContent() {
    let timer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(initAll, 150);
    });
    ['Main-Cart-Details', 'Cart-Drawer-Details'].forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el, { childList: true, subtree: true });
    });
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initAll(); observeCartContent(); });
  } else {
    initAll();
    observeCartContent();
  }

  document.addEventListener('shopify:section:load', () => setTimeout(initAll, 100));

})();
